import { motion } from "framer-motion";
import { ArrowUpRight } from "lucide-react";
import WhatsAppHeroCard from "./WhatsAppHeroCard";

const ease = [0.22, 1, 0.36, 1];

export default function Hero() {
  return (
    <section
      className="relative overflow-hidden pt-10 pb-14 lg:pt-16 lg:pb-24 bg-brand-paper grain"
      data-testid="hero-section"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-14 items-center">
          {/* LEFT column */}
          <motion.div
            initial="hidden"
            animate="visible"
            variants={{
              hidden: { opacity: 1 },
              visible: { opacity: 1, transition: { staggerChildren: 0.12 } },
            }}
            className="lg:col-span-7 lg:pt-4"
          >
            <motion.div
              variants={{ hidden: { opacity: 0, y: 20 }, visible: { opacity: 1, y: 0, transition: { duration: 0.6, ease } } }}
              className="inline-flex items-center gap-2 bg-brand-inverse text-brand-onInverse pl-1.5 pr-3 py-1.5 rounded-full text-[11px] font-semibold tracking-wide mb-6"
            >
              <span className="w-5 h-5 rounded-full flex items-center justify-center" style={{ background: 'var(--brand-green-neon)' }}>
                <svg className="w-3 h-3" viewBox="0 0 24 24" fill="#0A0A0A" aria-hidden="true">
                  <path d="M20.52 3.449C18.24 1.17 15.24 0 12.04 0 5.46 0 .14 5.32.14 11.9c0 2.09.54 4.13 1.57 5.94L.03 24l6.36-1.67a11.89 11.89 0 0 0 5.64 1.44h.01c6.58 0 11.9-5.32 11.9-11.9 0-3.18-1.26-6.16-3.42-8.42ZM12.04 21.8h-.01a9.88 9.88 0 0 1-5.03-1.38l-.36-.21-3.78 1 1.01-3.68-.24-.38A9.85 9.85 0 0 1 2.13 11.9C2.14 6.45 6.6 2 12.05 2c2.66 0 5.15 1.04 7.03 2.92a9.83 9.83 0 0 1 2.91 7.01c-.01 5.46-4.46 9.87-9.95 9.87Z"/>
                  <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.372-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347Z"/>
                </svg>
              </span>
              ANGEBOT IN 24H · PER WHATSAPP
            </motion.div>

            <motion.h1
              variants={{ hidden: { opacity: 0, y: 32 }, visible: { opacity: 1, y: 0, transition: { duration: 0.8, ease } } }}
              className="font-heading font-black text-brand-ink tracking-[-0.035em] leading-[0.92] text-[clamp(2.75rem,7vw,5.25rem)]"
            >
              Strom & Gas sparen –<br />
              Angebot{" "}
              <span className="energy-underline">in&nbsp;24&nbsp;Stunden.</span>
            </motion.h1>

            <motion.p
              variants={{ hidden: { opacity: 0, y: 20 }, visible: { opacity: 1, y: 0, transition: { duration: 0.6, ease, delay: 0.1 } } }}
              className="mt-7 text-base sm:text-lg text-brand-muted max-w-xl leading-relaxed"
            >
              Sende uns einfach deine aktuelle Abrechnung per WhatsApp – wir finden den besten
              Tarif für dich. Kostenlos, unverbindlich und ohne Papierkram.
            </motion.p>

            {/* Stat ticker */}
            <motion.div
              variants={{ hidden: { opacity: 0, y: 20 }, visible: { opacity: 1, y: 0, transition: { duration: 0.6, ease, delay: 0.2 } } }}
              className="mt-10 grid grid-cols-3 gap-4 sm:gap-8 max-w-lg"
            >
              <Stat value="1.000+" label="Tarife verglichen" />
              <Stat value="24h" label="Angebot im Chat" />
              <Stat value="0 €" label="Keine Kosten" />
            </motion.div>

            {/* Scroll cue */}
            <motion.div
              variants={{ hidden: { opacity: 0 }, visible: { opacity: 1, transition: { duration: 0.5, delay: 0.5 } } }}
              className="mt-10 hidden lg:flex items-center gap-3 text-xs text-brand-muted"
            >
              <span className="inline-block w-8 h-px bg-brand-ink" />
              <span className="uppercase tracking-[0.25em] font-semibold">Scroll</span>
              <ArrowUpRight className="w-3.5 h-3.5 rotate-90" />
            </motion.div>
          </motion.div>

          {/* RIGHT — WhatsApp CTA Card */}
          <div id="start" className="lg:col-span-5">
            <WhatsAppHeroCard />
          </div>
        </div>
      </div>
    </section>
  );
}

function Stat({ value, label }) {
  return (
    <div className="border-l-2 border-brand-ink pl-4">
      <div className="font-heading text-2xl sm:text-3xl font-black text-brand-ink tracking-tighter leading-none">{value}</div>
      <div className="mt-2 text-[11px] uppercase tracking-[0.15em] font-semibold text-brand-muted">{label}</div>
    </div>
  );
}
